import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from "@/hooks/useAuth";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import Layout from '@/components/layout/Layout';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await login(email.trim(), password.trim());
            // Navigate immediately - do not wait for background profile sync
            toast.success("Login successful!");
            navigate('/my-courses');
        } catch (error: unknown) {
            console.error("Login Error Catch:", error);
            const message = error instanceof Error ? error.message : 'Invalid credentials';

            if (message.toLowerCase().includes("email not confirmed")) {
                toast.error("Please confirm your email or contact system admin.", {
                    description: "Verification link sent to your inbox."
                });
            } else {
                toast.error(message);
            }
        } finally {
            setIsLoading(false);
        }

    };

    return (
        <Layout>
            <div className="flex items-center justify-center bg-background px-4 relative overflow-hidden py-10">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px] z-0" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px] z-0" />

                <div className="w-full max-w-md relative z-10">
                    <div className="card-premium !p-10 bg-white/40 border-white/60">
                        <div className="flex flex-col items-center mb-10 text-center">
                            <div className="w-20 h-20 rounded-[2rem] bg-white shadow-premium flex items-center justify-center mb-6 border border-white/60 group">
                                <Logo showText={false} size={48} className="transition-transform duration-500 group-hover:scale-110" />
                            </div>
                            <h1 className="text-3xl font-black text-primary uppercase tracking-tighter mb-2">Login</h1>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/40">Login to your account</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-8">
                            <div className="space-y-3">
                                <Label htmlFor="email" className="text-[10px] font-black uppercase tracking-widest text-primary/60 ml-1">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="yourname@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="h-14 rounded-2xl bg-white/50 border-white/80 focus:bg-white focus:ring-accent/20 transition-all duration-300 px-6 font-medium"
                                />
                            </div>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center px-1">
                                    <Label htmlFor="password" className="text-[10px] font-black uppercase tracking-widest text-primary/60">Password</Label>
                                    <Link to="/forgot-password" title="Recover access" className="text-[10px] font-black uppercase tracking-widest text-accent hover:text-gold-dark transition-colors">
                                        Forgot password?
                                    </Link>
                                </div>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="h-14 rounded-2xl bg-white/50 border-white/80 focus:bg-white focus:ring-accent/20 transition-all duration-300 px-6 pr-12 font-medium"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-primary/40 hover:text-primary transition-colors focus:outline-none"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <Button type="submit" className="w-full h-16 btn-navy text-[10px] font-black uppercase tracking-widest shadow-premium" disabled={isLoading}>
                                {isLoading ? 'Logging in...' : 'Login'}
                            </Button>
                        </form>

                        <div className="mt-10 pt-8 border-t border-gray-100/50 text-center">
                            <span className="text-[10px] font-black uppercase tracking-widest text-primary/40">New here? </span>
                            <Link to="/register" className="text-[10px] font-black uppercase tracking-widest text-accent hover:text-gold-dark transition-colors ml-2">
                                Create account
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Login;
